import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useLayoutEffect
} from 'react';
import Card from 'components/Card';
import useAlerts from 'customHook/useAlerts';
import AlertItem from '../AlertItem';
import { useMutation } from '@apollo/client';
import { MARK_ALL_ROOM_ALERTS_AS_READ } from 'graphql/service';
import ModalClearAll from 'screens/ModalClearAll';
import { Container, AlertContainer, Spinner } from './styles';
import { debouncedErrorMessage } from 'utils/messageUtil';

const Alerts = ({ mobile }) => {

  const pageNumberRef = useRef(1);
  const hasMoreRef = useRef(true);

  const {
    markRoomAlertAsRead,
    markRoomAlertAsUnread,
    pageSize,
    offset,
    setOffset,
    fetchMore,
    loading,
    alerts,
    setAlerts
  } = useAlerts();

  const listRef = useRef(null);

  // ✅ NEW: store scroll position
  const savedScrollTopRef = useRef(0);
  const shouldRestoreScrollRef = useRef(false);

  useEffect(() => {
    pageNumberRef.current = Math.floor(offset / pageSize) + 1;
  }, [offset, setOffset, pageSize]);

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) return;

    const handleScroll = () => {
      savedScrollTopRef.current = listElement.scrollTop;

      const { scrollTop, clientHeight, scrollHeight } = listElement;
      const scrolledToBottom =
        scrollHeight - scrollTop <= clientHeight + 100;

      if (scrolledToBottom && !loading && hasMoreRef.current) {
        shouldRestoreScrollRef.current = true;

        fetchMore({
          variables: {
            pageSize,
            pageNumber: pageNumberRef.current + 1,
            showRead: true,
            orderBy: {
              field: 'createdAt',
              direction: 'desc'
            }
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (
              !fetchMoreResult ||
              !fetchMoreResult.allRoomAlerts.length
            ) {
              hasMoreRef.current = false;
              return prev;
            }

            const existingIds = new Set(
              prev.allRoomAlerts.map(a => a.alertId)
            );
            const newAlerts =
              fetchMoreResult.allRoomAlerts.filter(
                a => !existingIds.has(a.alertId)
              );

            return {
              ...prev,
              allRoomAlerts: [...prev.allRoomAlerts, ...newAlerts]
            };
          }
        })
          .then(() => {
            pageNumberRef.current += 1;
          })
          .catch(error => {
            if (!error.networkError) {
              let content = error.message;
              if (error.message.includes('Validation')) {
                content = JSON.stringify(
                  error.graphQLErrors[0]?.extensions?.exception
                );
              }
              debouncedErrorMessage(content);
            }
          });
      }
    };

    listElement.addEventListener('scroll', handleScroll);
    return () =>
      listElement.removeEventListener('scroll', handleScroll);
  }, [loading, pageSize, fetchMore]);

  useLayoutEffect(() => {
    if (!shouldRestoreScrollRef.current) return;

    const el = listRef.current;
    if (!el) return;

    el.scrollTop = savedScrollTopRef.current;
    shouldRestoreScrollRef.current = false;
  }, [alerts]);

  const [markAllRoomAlertsAsRead, { loading: markAllRoomAlertsAsReadLoading }] =
    useMutation(MARK_ALL_ROOM_ALERTS_AS_READ, {
      onCompleted: () => {
        setAlerts(prev =>
          prev.map(alert => ({
            ...alert,
            isReadByCurrentUser: true
          }))
        );
      },
      onError: error => {
        if (!error.networkError) {
          let content = error.message;
          if (error.message.includes('Validation')) {
            content = JSON.stringify(
              error.graphQLErrors[0]?.extensions?.exception
            );
          }
          debouncedErrorMessage(content);
        }
      }
    });

  const sortedAlerts = useMemo(() => {
    const unread = alerts.filter(a => !a.isReadByCurrentUser);
    const read = alerts.filter(a => a.isReadByCurrentUser);
    return [...unread, ...read];
  }, [alerts]);

  if (loading) return <p />;

  return (
    <Container mobile={mobile}>
      <Card
        additionalstyle={`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: flex-start;
        `}
      >
        <div className="title">ALERTS</div>
        <ModalClearAll
          buttonStyle={`width: 80px;`}
          loading={markAllRoomAlertsAsReadLoading}
          clearFunction={markAllRoomAlertsAsRead}
          type="alert"
        />
      </Card>

      <AlertContainer mobile={mobile} ref={listRef}>
        {sortedAlerts.map(alert => (
          <AlertItem
            key={alert.alertId}
            mobile={mobile}
            markRoomAlertAsRead={markRoomAlertAsRead}
            markRoomAlertAsUnread={markRoomAlertAsUnread}
            alertId={alert.alertId}
            roomNumber={alert.room?.roomNumber}
            groupId={
              alert.roomBooking?.booking?.group?.groupId
            }
            description={alert.description}
            type={alert.type}
            originator={alert.originator}
            createdAt={alert.createdAt}
            isRead={alert.isReadByCurrentUser}
            setAlerts={setAlerts}
          />
        ))}

        {!hasMoreRef.current ||
        sortedAlerts.length === 0 ? (
          <span className="noMoreAlerts">
            There is nothing else here.
          </span>
        ) : loading ? (
          <Spinner />
        ) : null}
      </AlertContainer>
    </Container>
  );
};

export default Alerts;
